import { useEffect, useState } from "react";

import { useSessionAuthState } from "@/hooks/useSession";
import {
  CLI_ACCOUNT_URL,
  CLI_SIGNUP_URL,
  signupCtaLabel,
  useCanonicalAuthUrl,
} from "@/landing/lib/constants";

/**
 * SSR-safe auth-aware CTA for the WAB/Steward marketing chrome. Mirrors the
 * CliNav gate (NavAuthGate): the session providers are mounted client-only
 * (ClientProviders returns bare children on the server), so reading the
 * session during SSR / first hydration render throws. The `isClient` guard
 * renders the signed-out variant on the server + first paint, then swaps after
 * mount. Signed-out → /signup; signed-in → /account ("Open app").
 *
 * Class-driven (not the shadcn Button) so each call keeps its own wab.css recipe
 * (.nav-cta / .mm-cta / .btn / .hero-cta / .prefooter-cta) and the `→` arrow.
 */
type AuthCtaProps = {
  className?: string;
  signedOutLabel?: string;
  signedInLabel?: string;
  onClick?: () => void;
};

export function AuthCta(props: AuthCtaProps) {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  if (!isClient) return <SignedOutCta {...props} />;
  return <AuthCtaClient {...props} />;
}

function AuthCtaClient(props: AuthCtaProps) {
  const { isSignedIn } = useSessionAuthState();
  if (isSignedIn) return <SignedInCta {...props} />;
  return <SignedOutCta {...props} />;
}

function SignedOutCta({
  className,
  signedOutLabel = "Try now",
  onClick,
}: AuthCtaProps) {
  const signupUrl = useCanonicalAuthUrl(CLI_SIGNUP_URL);

  return (
    <a href={signupUrl} className={className} onClick={onClick}>
      {signupCtaLabel(signedOutLabel)} <span className="arr">→</span>
    </a>
  );
}

function SignedInCta({
  className,
  signedInLabel = "Open app",
  onClick,
}: AuthCtaProps) {
  const accountUrl = useCanonicalAuthUrl(CLI_ACCOUNT_URL);

  return (
    <a href={accountUrl} className={className} onClick={onClick}>
      {signedInLabel} <span className="arr">→</span>
    </a>
  );
}
